import type {
  GridAggregationModel,
  GridCellParams,
  GridColumnOrderChangeParams,
  GridSortModel,
} from "@mui/x-data-grid-premium";
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { withErrorHandling } from "@entrilia/shared/api/axiosHelper.ts";
import type { ApiResponse } from "@entrilia/shared/api/types.ts";
import { useNotificationContext } from "@entrilia/shared/contexts/NotificationContext.ts";
import { logError } from "@entrilia/shared/logging.ts";
import type { AnyObject } from "@entrilia/shared/types.ts";
import { generateGuid } from "@entrilia/shared/utilities/generateGuid.ts";
import biApi from "../../../../../../../../api/biApi.ts";
import {
  ItemDataType,
  type DrillDownCell,
  type DrillDownDataRequest,
  type DrillDownRow,
  type MeasureDrillDownInfo,
  type MetricDrillDownInfo,
} from "../../../../../../../../api/types/svTypes.ts";
import { sortOrderedFields } from "../../../../hooks/sortOrderedFields";
import type { DrillDownState } from "../../../../hooks/useDrillDown";
import {
  useGridDataSourceWithRangePaging,
  type FetchRowsPayload,
  type GridDataSourceWithRangePagingResponse,
} from "@entrilia/shared/components/reporting/text-filter-grid/useGridDataSourceWithRangePaging.ts";
import { selectLogQueries } from "../../../../store/selectors/devToolsSelectors";
import { selectDimensions } from "../../../../store/selectors/metaDataSelectors";
import { createDevToolsAction } from "../../../../store/utils/storeUtils";
import TransactionAttachmentsDialog from "./dialogs/TransactionAttachmentsDialog.tsx";
import DrillDownGrid from "./DrillDownGrid.tsx";
import { useDrillDownGridScroll } from "./DrillDownGridScrollContext.tsx";
import { isMetricDrillDownInfo } from "./isMeasureDrillDownInfo.ts";
import { getExtensionLedger } from "./ledger/extensionLedgers.ts";
import {
  createColumns,
  getDrillDownColumns,
  rebuildColumnsOrder,
  rebuildColumnsSorting,
} from "./utils/drillDownHelper.tsx";

interface Props {
  state: DrillDownState;
  info: MeasureDrillDownInfo | MetricDrillDownInfo;
  onDrillDown?: (row: DrillDownRow) => void;
  onSelectedFieldsChanged?: (fields: DrillDownState["selectedFields"]) => void;
  onSortedFieldsChanged?: (fields: DrillDownState["sortedFields"]) => void;
  disableCellClick?: boolean;
  highlightedColumns: string[] | undefined;
}

const getDrillDownData = withErrorHandling(biApi.getDrillDownData);

const toDrillDownRow = (cells: DrillDownCell[]): DrillDownRow => ({
  id: generateGuid(),
  cells: Object.fromEntries(cells.map((cell) => [cell.fieldName, cell])),
});

const getDrillDownAggregatedValue = (row: DrillDownRow, field: string) =>
  row.cells?.[field]?.formattedValue ?? row.cells?.[field]?.value;

interface DrillDownPayload extends AnyObject {
  drillDownConfig: Omit<DrillDownDataRequest, "range" | "sortingColumns" | "aggregationColumns">;
}

const DrillDownGridWrapper = ({
  state,
  onDrillDown,
  info,
  onSelectedFieldsChanged,
  onSortedFieldsChanged,
  disableCellClick,
  highlightedColumns,
}: Props) => {
  const dimensions = useSelector(selectDimensions);
  const logQueries = useSelector(selectLogQueries);
  const dispatch = useDispatch();
  const { sendNotificationError } = useNotificationContext();
  const stateRef = useRef<DrillDownState>(state);
  useEffect(() => {
    stateRef.current = state;
  }, [state]);

  const [attachedFilesTransactionNo, setAttachedFilesTransactionNo] = useState<string | undefined>(undefined);

  const sortModel = useMemo((): GridSortModel => {
    return [...state.sortedFields].sort(sortOrderedFields).map((f) => {
      return { field: f.field.name, sort: f.sortAsc ? "asc" : "desc" };
    });
  }, [state.sortedFields]);

  const aggregationModel = useMemo((): GridAggregationModel => {
    const model: GridAggregationModel = {};
    getDrillDownColumns(state.selectedFields.filter((f) => f.field.type === ItemDataType.Numeric)).forEach((c) => {
      model[c.dimensionName] = "custom";
    });
    return model;
  }, [state.selectedFields]);

  const fetchDrillDownRows = useCallback(
    async (
      params: FetchRowsPayload<DrillDownPayload>
    ): Promise<ApiResponse<GridDataSourceWithRangePagingResponse<DrillDownRow>>> => {
      const body: DrillDownDataRequest = {
        ...params.drillDownConfig,
        sortingColumns: params.sortModel.map((s) => ({
          dimensionName: s.field,
          sortAsc: s.sort === "asc",
          caption: s.field,
        })),
        range: params.range,
        useQueryLogging: logQueries === true,
      };

      const [response, error] = await getDrillDownData(body, params.signal);
      if (error) {
        if (error.isCanceledRequest) {
          return { success: false };
        }
        logError(error, "[DrillDownGridWrapper] fetchDrillDownRows");
        sendNotificationError(error.message);
        return {
          success: false,
          error: error,
        };
      }

      if (logQueries === true && response.loggingItems !== undefined) {
        dispatch(createDevToolsAction("update", { loggingItems: response.loggingItems }));
      }

      return {
        success: true,
        data: {
          items: response.items.map(toDrillDownRow),
          total: response.total,
          grandTotalItem: toDrillDownRow(response.grandTotalItem),
        },
      };
    },
    [dispatch, logQueries, sendNotificationError]
  );

  const drillDownPayload = useMemo<DrillDownPayload>(() => {
    return {
      drillDownConfig: {
        allocated: state.drillDownConfig.allocated,
        columns: state.drillDownConfig.columns,
        conditions: state.drillDownConfig.conditions,
        table: state.drillDownConfig.table,
        cacheSessionId: state.drillDownConfig.cacheSessionId,
      },
    };
  }, [
    state.drillDownConfig.allocated,
    state.drillDownConfig.table,
    state.drillDownConfig.columns,
    state.drillDownConfig.conditions,
    state.drillDownConfig.cacheSessionId,
  ]);

  const { gridApiRef, dataSource, totalRows } = useGridDataSourceWithRangePaging(
    fetchDrillDownRows,
    drillDownPayload,
    getDrillDownAggregatedValue
  );

  const gridColumns = useMemo(
    () => createColumns(state.selectedFields, highlightedColumns, setAttachedFilesTransactionNo),
    [state.selectedFields, highlightedColumns]
  );

  useEffect(() => {
    if (totalRows !== undefined) {
      stateRef.current.actions.setTotalRows(totalRows);
    }
  }, [totalRows]);

  // MUI DataGrid can keep focus on a column that is no longer present after column setup changes.
  // Clearing focus prevents getCellParams/getRowValue crashes for removed fields.
  useLayoutEffect(() => {
    gridApiRef.current?.setState((gridState) => ({
      ...gridState,
      focus: { cell: null, columnHeader: null, columnHeaderFilter: null, columnGroupHeader: null },
    }));
  }, [gridColumns, gridApiRef]);

  const { scrollRequest, setVisibleColumnFields } = useDrillDownGridScroll();

  useEffect(() => {
    setVisibleColumnFields(gridColumns.map((col) => col.field));
    return () => setVisibleColumnFields([]);
  }, [gridColumns, setVisibleColumnFields]);

  // React to a scroll-to-field request raised by a title source-column link.
  useEffect(() => {
    if (!scrollRequest) {
      return;
    }
    const fieldIndex = gridColumns.findIndex((col) => col.field === scrollRequest.field);
    if (fieldIndex < 0) {
      return;
    }

    const api = gridApiRef.current;
    if (!api) {
      return;
    }
    const scroller = api.rootElementRef.current?.querySelector<HTMLElement>(".MuiDataGrid-virtualScroller");
    if (!scroller) {
      api.scrollToIndexes({ colIndex: fieldIndex });
      return;
    }

    // Compute the target column's left offset to decide which side we're scrolling from.
    const apiCols = api.getAllColumns();
    let columnLeft = 0;
    for (let i = 0; i < fieldIndex; i++) {
      columnLeft += apiCols[i]?.computedWidth ?? apiCols[i]?.width ?? 0;
    }

    // Aim one column past the target so it doesn't sit flush against the edge it came
    // from: column to the left → scroll left, pad on left; otherwise pad on right.
    const targetIndex =
      columnLeft < scroller.scrollLeft ? Math.max(fieldIndex - 1, 0) : Math.min(fieldIndex + 1, gridColumns.length - 1);
    api.scrollToIndexes({ colIndex: targetIndex });
  }, [scrollRequest, gridColumns, gridApiRef]);

  const handleSortingChanged = useCallback(
    (model: GridSortModel) => {
      const newSorting = rebuildColumnsSorting(model, dimensions);
      if (onSortedFieldsChanged) {
        onSortedFieldsChanged(newSorting);
        return;
      }
      stateRef.current.actions.changeFieldSorting(newSorting);
    },
    [dimensions, onSortedFieldsChanged]
  );

  // Rows can be drilled into only from a non-allocated GL/Memo ledger (→ their allocated entries).
  // Allocated ledgers and the GL extension ledgers (AP/AR, VAT) have nothing further to drill into,
  // and metric/persisted-aggregation grids aren't clickable at all. Driving both the cursor and the
  // click from one flag keeps the pointer affordance and the actual behavior in sync.
  const supportsRowDrillDown = useMemo(() => {
    if (disableCellClick || isMetricDrillDownInfo(info)) {
      return false;
    }
    return !info.allocated && getExtensionLedger(info.table) === undefined;
  }, [disableCellClick, info]);

  const handleCellClick = useCallback(
    (params: GridCellParams<DrillDownRow>) => {
      if (!supportsRowDrillDown) {
        return;
      }

      const entryNo = params.row?.cells?.["EntryNo"]?.value;
      if (entryNo != null) {
        onDrillDown?.call(null, params.row);
      }
    },
    [onDrillDown, supportsRowDrillDown]
  );

  const handleColumnOrderChange = useCallback(
    (params: GridColumnOrderChangeParams) => {
      const reorderedFields = rebuildColumnsOrder(params, stateRef.current.selectedFields);
      if (onSelectedFieldsChanged) {
        onSelectedFieldsChanged(reorderedFields);
        return;
      }
      stateRef.current.actions.changeSelectedFields(reorderedFields);
    },
    [onSelectedFieldsChanged]
  );

  return (
    <>
      <DrillDownGrid
        columns={gridColumns}
        apiRef={gridApiRef}
        dataSource={dataSource}
        onSortingChanged={handleSortingChanged}
        sortModel={sortModel}
        onCellClicked={handleCellClick}
        selectableRows={supportsRowDrillDown}
        onColumnOrderChange={handleColumnOrderChange}
        aggregationModel={aggregationModel}
      />
      {attachedFilesTransactionNo !== undefined && (
        <TransactionAttachmentsDialog
          transactionNo={attachedFilesTransactionNo}
          onClose={() => setAttachedFilesTransactionNo(undefined)}
        />
      )}
    </>
  );
};

export default DrillDownGridWrapper;
